import { AgropilotRole, DocumentedApp, UserPermissions } from '@agroone/entities'

/**
 * Business rules for the functional documentation of the front apps.
 */

/** What the rules read from a user: the shape shared by the back-end `UserDto` and the front-end current user. */
export interface DocumentationReader {
  permissions?: (UserPermissions | string)[]
  agropilotRole?: AgropilotRole | null
}

/**
 * Permissions that open each app, as its own entry points check them: the hub cards of agroone-app and the
 * screens of Masterdata. Agropilot has no permission of its own: any Agropilot role opens it.
 */
const APP_ACCESS_PERMISSIONS: Record<Exclude<DocumentedApp, DocumentedApp.AGROPILOT>, UserPermissions[]> = {
  [DocumentedApp.AGROONE_APP]: [
    UserPermissions.CROP_MODULE_ACCESS,
    UserPermissions.PLANTING_MODULE_ACCESS,
    UserPermissions.USER_MODULE_ACCESS,
  ],
  [DocumentedApp.MASTERDATA]: [
    UserPermissions.MASTERDATA_MODULE_ACCESS,
    UserPermissions.MASTERDATA_MANAGER,
    UserPermissions.MASTERDATA_SPECIFICATION,
    UserPermissions.MASTERDATA_CSR_EDITION,
  ],
}

export const DocumentationRules = {
  /**
   * A user reads the documentation of an app they can open: the page describes every screen of the app,
   * including the ones their role does not show.
   */
  canRead(user: DocumentationReader | null | undefined, app: DocumentedApp): boolean {
    if (!user) {
      return false
    }

    if (app === DocumentedApp.AGROPILOT) {
      return !!user.agropilotRole
    }

    const userPermissions: (UserPermissions | string)[] = user.permissions ?? []
    return APP_ACCESS_PERMISSIONS[app].some((permission: UserPermissions) => userPermissions.includes(permission))
  },
}
